import type { ReactNode } from 'react';
import { useIsNarrowViewport } from '@/hooks/workbench/useIsNarrowViewport';
import { CardHeader } from './card/CardHeader';
import { CustomerColumn } from './card/CustomerColumn';
import { FeedColumn } from './card/FeedColumn';
import type { WorkbenchProduct, WorkbenchEditState, ReviewDecision } from '@/hooks/useWorkbench';

interface WorkbenchProductCardProps {
  product: WorkbenchProduct;
  editState: WorkbenchEditState;
  reviewDecision: ReviewDecision;
  /**
   * Steht auf diesem Produkt ein Handkorrektur-Stand?
   *
   * VEREINIGUNG zweier Signale, die einzeln beide luegen (Herleitung an der
   * Aufrufstelle in `WorkbenchReviewer`): `wasEdited` wird erst NACH einem
   * erfolgreichen DB-Write wahr, waehrend des Tippens ist es falsch;
   * `hasUnsavedChanges` faellt nach dem Auto-Save wieder auf falsch, obwohl
   * der Handstand besteht.
   */
  hasManualEdit: boolean;
  onEditChange: (field: keyof WorkbenchEditState, value: string | string[]) => void;
  /** 1-basierte Position im Stapel, fuer den Kopf. */
  position: number;
  total: number;
  /** true = die Gesamtzahl ist nur eine Untergrenze. */
  totalUnknown?: boolean;
  /**
   * Die Entscheidungsleiste. Sie sitzt am Fuss DER KARTE, nicht mehr am
   * Fensterrand: die Karte besitzt damit ihr Layout, waehrend der Reviewer die
   * Handler behaelt.
   */
  footer?: ReactNode;
}

function getBorderClass(decision: ReviewDecision, hasManualEdit: boolean): string {
  if (decision === 'approved') return 'border-success/30';
  if (decision === 'rejected') return 'border-destructive/30';
  if (hasManualEdit) return 'border-warning/30';
  return 'border-border';
}

/**
 * Die Werkbank-Karte, Richtung C2.
 *
 * Links steht, was der Kunde sieht. Rechts steht, was wirklich im Feed
 * landet. Unten steht, was du tust.
 *
 * Was hier abgebaut wurde und warum (Spec 13):
 *   - `ImpactLine` und der aufklappbare Score-Bereich gehen in der permanenten
 *     Punkte-Spalte auf; die Punktzahl stand vorher dreimal auf einer Karte,
 *     eine davon erst nach einem Klick.
 *   - Der Thumbnail im Kopf entfaellt: das einzige Bild gehoert in die Kachel,
 *     wo es zeigt, wie das Produkt beim Kunden ankommt.
 *   - Die Pille "Offen" entfaellt: sie sagt dasselbe wie der aktive Reiter.
 *
 * Schmale Bildschirme bekommen KEINE Reiter mehr, sondern den Stapel. Die
 * Reiter gab es, weil die Attributspalte sonst unter zwei Bildschirmhoehen
 * Wort-Diff lag. In C2 ist die linke Spalte hoehenbegrenzt (Kachel plus
 * Punkte) und die rechte die lange: gestapelt steht damit das Kurze oben und
 * das Lange unten, und niemand muss einen Reiter finden.
 *
 * Breiter Zweig, seit T6.1 (08.09.2026): GENAU EIN Scrollbereich. Vorher
 * trugen linke UND rechte Spalte je ein eigenes `overflow-y-auto` - "links
 * ist fest, scrollt nicht" stimmte nur, solange ihr Inhalt kuerzer war als
 * die Kartenhoehe. Jetzt scrollt die Karte als Ganzes, die linke Spalte
 * faehrt mit.
 */
export function WorkbenchProductCard({
  product,
  editState,
  reviewDecision,
  hasManualEdit,
  onEditChange,
  position,
  total,
  totalUnknown,
  footer,
}: WorkbenchProductCardProps) {
  const isNarrow = useIsNarrowViewport();

  /* Die `key`s auf beiden Spalten sind kein Listen-Key, sondern ein
     Zustands-Reset: die Aufklapper und die Anpassen-Felder halten ihren
     Zustand lokal. Ohne den Wechsel der Identitaet ueberlebt er den
     Produktwechsel, und ab dem ersten "Anpassen" saehe jedes Folgeprodukt sein
     Eingabefeld statt des Diffs - genau die Umkehrung von "Bearbeiten ist
     Nebenhandlung". Bewusst hier und nicht als `key` am Aufrufer: die
     Zusicherung gehoert zu dem Bauteil, das den Zustand haelt, und ueberlebt
     damit einen zweiten Aufrufer, der den Key vergisst. */
  const customer = (
    <CustomerColumn
      key={`customer-${product.id}`}
      product={product}
      editState={editState}
      hasManualEdit={hasManualEdit}
    />
  );
  const feed = (
    <FeedColumn
      key={`feed-${product.id}`}
      product={product}
      editState={editState}
      onEditChange={onEditChange}
    />
  );

  return (
    <div
      data-testid="workbench-product-card"
      className={`flex h-full flex-col overflow-hidden rounded-lg border bg-card shadow-sm ${getBorderClass(reviewDecision, hasManualEdit)}`}
    >
      <CardHeader
        product={product}
        optimizedTitle={editState.optimized_title}
        reviewDecision={reviewDecision}
        position={position}
        total={total}
        totalUnknown={totalUnknown}
      />

      {isNarrow ? (
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {customer}
            {feed}
          </div>
        </div>
      ) : (
        /* T6.1 (08.09.2026): GENAU EIN Scrollbereich statt zwei. Vorher trug
           jede Spalte ihr eigenes `overflow-y-auto` - zwei unabhaengige
           Scrollleisten auf einer Karte, und "links scrollt nicht" stimmte
           nur, solange die linke Spalte kuerzer war als die Kartenhoehe. Der
           AEUSSERE Container traegt den Scroll, beide Spalten sind reine
           Flex-Kinder ohne eigene Overflow-Regel.
           Trennstrich ueber die volle Hoehe: `items-stretch` ist die
           Flex-Vorbelegung, aber eine Spalte mit eigenem Inhaltsfluss
           koennte sich mit `h-full` gegen den jetzt scrollenden Elternteil
           auf `auto` aufloesen (100% von "auto" ist wieder "auto") und
           dann kuerzer sein als die Nachbarspalte, der `border-r` endete
           dann mitten in der Karte. Keine der beiden Spalten setzt `h-full`
           oder eine eigene Hoehe, `self-stretch` (= Default von
           `items-stretch`) reicht deshalb: beide Spalten sind exakt so
           hoch wie der hoehere Nachbar, der Strich laeuft durch. */
        <div className="flex min-h-0 flex-1 overflow-y-auto">
          {/* Links: der Bezugspunkt, faehrt mit dem Rest der Karte mit.
              B9 (Spec 5.2 nennt woertlich "Linke Spalte 240 px"): die
              Kachel (`ShoppingTile`, `w-full`-Bild, auch bei `kachelBild:
              'gross'` fix 192 px HOCH, nicht breit) und die Punkte-Zeile
              (`ScorePanel`, Flex-Text ohne feste Breiten) skalieren beide
              mit der Spaltenbreite und reissen bei 240 px nicht - geprueft
              gegen den 208-px-Innenraum nach Abzug von `p-4` (16 px je
              Seite). */}
          <div className="w-[240px] flex-shrink-0 border-r border-border p-4">
            {customer}
          </div>
          <div className="min-w-0 flex-1 p-4">{feed}</div>
        </div>
      )}

      {footer}
    </div>
  );
}
